import { fmtMoney } from '@/features/shared/format'
import i18n from '@/i18n'
import { downloadFile } from '@/lib/api/client'
import { bordroKesintiler } from '../map'
import type { BordroSatir } from '../types'
import { csvAmount, exportCsv, fileStamp, printDoc } from './exportActions'

const money = (n: number) => n.toFixed(2)

/** Kazanç/kesinti dökümü sunucuda dönem parametreleriyle hesaplanan değerlerden gelir; döküm yoksa toplam kesinti tek satırdır. */
export function bordroBreakdown(r: BordroSatir) {
  const t = i18n.t.bind(i18n)
  const d = r.dokum
  return {
    kazanc: [
      { label: t('hr.personel.ucret.brut'), amount: r.brut },
      { label: t('hr.personel.bordro.ek'), amount: r.ek },
    ],
    kazancToplam: money(Number(r.brut) + Number(r.ek)),
    kesinti: d ? bordroKesintiler(d) : [{ label: t('hr.personel.ucret.kesintiler'), amount: r.kesinti }],
    istisna: d
      ? [
          { label: t('hr.personel.bordro.dokum.gvIstisna'), amount: d.gvIstisna },
          { label: t('hr.personel.bordro.dokum.dvIstisna'), amount: d.dvIstisna },
        ]
      : [],
    isveren: d
      ? [
          { label: t('hr.personel.bordro.dokum.sgkIsveren'), amount: d.sgkIsveren },
          { label: t('hr.personel.bordro.dokum.issizlikIsveren'), amount: d.issizlikIsveren },
          { label: t('hr.personel.bordro.dokum.tesvik', { rate: d.tesvikOrani }), amount: `-${d.tesvik}` },
        ]
      : [],
    isverenMaliyet: d?.isverenMaliyet,
    matrah: d ? { sgk: d.sgkMatrah, gv: d.gvMatrah, kumulatif: d.kumulatifMatrah } : undefined,
  }
}

/** Sunucuda üretilen bordro PDF'ini indirir; PDF üretici kullanılamıyorsa false döner (çağıran yazdırma görünümüne düşer). */
export async function downloadPayslip(employeeId: string, donem: string): Promise<boolean> {
  try {
    await downloadFile(`/api/v1/hr/payroll/employees/${employeeId}/payrolls/${donem}/payslip.pdf`, `bordro-${donem}.pdf`)
    return true
  } catch {
    return false
  }
}

export async function printBordro(r: BordroSatir, donem: string) {
  if (await downloadPayslip(r.personel_id, donem)) return
  printBordroView(r, donem)
}

/** Tarayıcı yazdırma görünümü (sunucu PDF'i alınamazsa). */
function printBordroView(r: BordroSatir, donem: string) {
  const t = i18n.t.bind(i18n)
  const b = bordroBreakdown(r)
  printDoc({
    title: `${t('hr.personel.actions.bordro.pdf')} · ${donem}`,
    subtitle: `${r.ad} · ${r.unvan} · ${r.departman}`,
    sections: [
      {
        heading: t('hr.personel.ucret.kazanclar'),
        rows: b.kazanc.map((k) => [k.label, fmtMoney(k.amount)]),
        total: [t('common.total', { defaultValue: 'Toplam' }), fmtMoney(b.kazancToplam)],
      },
      {
        heading: t('hr.personel.ucret.kesintiler'),
        rows: b.kesinti.map((k) => [k.label, fmtMoney(k.amount)]),
        total: [t('common.total', { defaultValue: 'Toplam' }), fmtMoney(r.kesinti)],
      },
      {
        rows: [
          [t('hr.personel.fields.sicil'), r.sicil],
          [t('hr.personel.columns.durum'), t(`hr.personel.bordro.durum.${r.durum}`)],
        ],
        total: [t('hr.personel.ucret.net'), fmtMoney(r.net)],
      },
      ...(b.isverenMaliyet
        ? [
            {
              heading: t('hr.personel.bordro.dokum.isverenTitle'),
              rows: b.isveren.map((k): [string, string] => [k.label, fmtMoney(k.amount)]),
              total: [t('hr.personel.bordro.dokum.isverenMaliyet'), fmtMoney(b.isverenMaliyet)] as [string, string],
            },
          ]
        : []),
    ],
  })
}

export function exportBordroCsv(rows: BordroSatir[], donem: string) {
  const t = i18n.t.bind(i18n)
  exportCsv(
    `bordro_${donem.replace(/\s+/g, '_').toLowerCase()}_${fileStamp()}.csv`,
    [
      t('hr.personel.fields.sicil'),
      t('hr.personel.columns.personel'),
      t('hr.personel.columns.departman'),
      t('hr.personel.columns.pozisyon'),
      t('hr.personel.ucret.brut'),
      t('hr.personel.bordro.ek'),
      t('hr.personel.ucret.kesintiler'),
      t('hr.personel.ucret.net'),
      t('hr.personel.columns.durum'),
    ],
    rows.map((r) => [
      r.sicil,
      r.ad,
      r.departman,
      r.unvan,
      csvAmount(r.brut),
      csvAmount(r.ek),
      csvAmount(r.kesinti),
      csvAmount(r.net),
      t(`hr.personel.bordro.durum.${r.durum}`),
    ]),
  )
}
